import {inject, Injectable, signal} from '@angular/core';
import {SlideshowControlService} from './slideshow-control.service';
import {AudioService} from '../audio/audio.service';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SlideProgressService {
  // Progress state
  public progressPercentage = signal(0);
  public currentTiming = signal(5000);
  // Services
  private slideshowControlService = inject(SlideshowControlService);
  private audioService = inject(AudioService);
  // Progress tracking state
  private currentSlideStartTime = 0;
  private pausedElapsed = 0; // Track elapsed time when paused
  private progressInterval?: number;

  constructor() {
  }

  /**
   * Start progress tracking for current slide
   */
  startProgressTracking(
    currentIndex: number,
    mediaItems: MediaItem[],
    isPlaying: () => boolean
  ): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }

    console.log('Starting progress tracking for slide', currentIndex + 1);

    // Only reset start time if we're not resuming from a pause
    if (this.pausedElapsed === 0) {
      this.currentSlideStartTime = Date.now();
    }
    // If resuming, the start time was already adjusted in play() method

    // Get optimal duration from the slideshow control service
    const optimalDuration = this.slideshowControlService.getOptimalImageDuration(mediaItems);
    this.currentTiming.set(optimalDuration);

    this.progressInterval = window.setInterval(() => {
      // Only update progress if audio is actually playing
      const audioIsPlaying = this.audioService.isPlaying();
      if (!audioIsPlaying) {
        // Audio is paused, don't advance progress - just return early
        return;
      }

      const elapsed = Date.now() - this.currentSlideStartTime;
      const duration = this.currentTiming();
      const slideProgress = Math.min((elapsed / duration) * 100, 100);

      // Calculate overall progress
      const totalSlides = mediaItems.length;
      const overallProgress = ((currentIndex + (slideProgress / 100)) / totalSlides) * 100;

      this.progressPercentage.set(overallProgress);

      // Debug progress (only log every 1 second to avoid spam)
      if (Math.floor(elapsed / 1000) !== Math.floor((elapsed - 50) / 1000)) {
        console.log(`Progress: ${overallProgress.toFixed(1)}% (slide ${currentIndex + 1}/${totalSlides}, ${slideProgress.toFixed(1)}% complete)`);
      }
    }, 50); // Update every 50ms for smooth progress
  }

  /**
   * Reset slide progress and start tracking for new slide
   */
  resetSlideProgress(
    currentIndex: number,
    mediaItems: MediaItem[],
    isPlaying: () => boolean,
    startSlideshowCallback: () => void
  ): void {
    this.currentSlideStartTime = Date.now();
    this.pausedElapsed = 0; // Reset paused time for new slide
    startSlideshowCallback();
    this.startProgressTracking(currentIndex, mediaItems, isPlaying);
  }

  /**
   * Handle play action - resume from where we left off
   */
  play(pausedElapsed: number): void {
    // Resume from where we left off by adjusting the start time
    if (pausedElapsed > 0) {
      this.currentSlideStartTime = Date.now() - pausedElapsed;
      console.log(`Resuming from ${pausedElapsed}ms elapsed time`);
    }
  }

  /**
   * Handle pause action - store elapsed time
   */
  pause(): number {
    // Store elapsed time when pausing so we can resume from the same point
    if (this.currentSlideStartTime > 0) {
      this.pausedElapsed = Date.now() - this.currentSlideStartTime;
      console.log(`Pausing at ${this.pausedElapsed}ms elapsed time`);
      return this.pausedElapsed;
    }
    return 0;
  }

  /**
   * Reset paused elapsed time (for manual navigation)
   */
  resetPausedElapsed(): void {
    this.pausedElapsed = 0;
  }

  /**
   * Handle progress bar click to jump to specific slide
   */
  handleProgressClick(
    event: MouseEvent,
    mediaItems: MediaItem[],
    currentIndex: () => number,
    setCurrentIndex: (index: number) => void,
    resetSlideProgressCallback: () => void
  ): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const targetIndex = Math.floor((percentage / 100) * mediaItems.length);

    const newIndex = Math.max(0, Math.min(targetIndex, mediaItems.length - 1));
    setCurrentIndex(newIndex);
    resetSlideProgressCallback();
  }

  /**
   * Pause slideshow progress tracking
   */
  pauseProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = undefined;
    }
  }

  /**
   * Get current paused elapsed time
   */
  getPausedElapsed(): number {
    return this.pausedElapsed;
  }

  /**
   * Set paused elapsed time
   */
  setPausedElapsed(elapsed: number): void {
    this.pausedElapsed = elapsed;
  }

  /**
   * Cleanup method to clear intervals
   */
  cleanup(): void {
    this.pauseProgressTracking();
  }
}
